import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useAtomValue, useSetAtom } from 'jotai';

import { showToast } from '@restworld/ui-ds-base';
import { useOrganizationsService } from '@restworld/data-services';
import { Company, ApiError } from '@restworld/utility-types';
import { QUERY_KEYS } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { useEmployerCompanyListQueryKey } from './use-employer-company-list-query';
import { isEmployerCompanyActionModalOpenAtom, isEmployerCompanyListModalOpenAtom } from '../employer-companies.atoms';

function useEmployerUpdateCompanyMutation(onSuccessCallback?: () => void) {
  const { t } = useTranslation('payment-and-invoices');
  const organisationsService = useOrganizationsService();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryKey = useEmployerCompanyListQueryKey();
  const allCompaniesQueryKey = useEmployerCompanyListQueryKey(true);
  const setIsEmployerCompanyActionModalOpen = useSetAtom(isEmployerCompanyActionModalOpenAtom);
  const isEmployerCompanyListModalOpen = useAtomValue(isEmployerCompanyListModalOpenAtom);

  const mutation = useMutation({
    mutationFn: (company: Partial<Company>) => organisationsService.updateCompany(company),
    onSuccess: () => {
      showToast(t('company_updated_successfully'), 'success', { position: 'top-right', autoClose: 3000 });

      // Invalidate all company list query keys
      queryClient.invalidateQueries({ queryKey });
      if (isEmployerCompanyListModalOpen) queryClient.invalidateQueries({ queryKey: allCompaniesQueryKey });
      // Also invalidate the query key used by usePaymentCompanyData
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANISATIONS.COMPANY_LIST, user?.organization_id],
      });

      // Call custom callback if provided (for JP onboarding flow), otherwise close default modal
      if (onSuccessCallback) {
        onSuccessCallback();
      } else {
        setIsEmployerCompanyActionModalOpen(false);
      }
    },
    onError: (error: ApiError) => {
      let errorMessage = t('something_went_wrong');
      if (error?.code === 422) {
        errorMessage = t('please_fill_in_all_required_fields_correctly');
      } else if (error?.code >= 500) {
        errorMessage = t('server_error');
      }
      showToast(errorMessage, 'error', { position: 'top-right', autoClose: 3000 });
    },
  });

  return mutation;
}

export default useEmployerUpdateCompanyMutation;
